import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom/cjs/react-router-dom'
import { Download, Eye, CircleAlert, CircleCheck } from 'lucide-react'
import { useOpenPDF } from '../../hooks/useOpenPDF'
import { pdfDisponible } from '../../utils/pdfDisponible'
import { useDateFormatter } from '../../hooks/useDateFormatter'
import { useCases } from '../../hooks/Cases/useCases'
import { useSetCase } from '../../hooks/Cases/useSetCase'
import { useAdminAPI } from '../../hooks/Admin/useAdminAPI'
import { fetchEstudiosTecnicoPorAlcance } from './Service/EvaluationService'
import Statistics from '../../assets/icons/statistics.svg'
import StatusArrow from '../../assets/icons/up-blue-arrow.svg'
import { EXAM_TYPE_TEXT } from './helpers'
import PatientInformation from '../../components/PatientInformation'
import { SessionExpired } from '../../components/SessionExpired'
import { SortableTable } from '../../components/SortableTable'
import { DiagnosisBadge } from '../../components/DiagnosisBadge'
import {
  Container,
  Header,
  TitleWrapper,
  TitleIcon,
  Title,
  MonthlyCasesWrapper,
  MonthlyTitle,
  MonthlyValues,
  MonthlyTotal,
  MonthlyPercentageWrapper,
  MonthlyPercentageIcon,
  MonthlyPercentage,
  Subheader,
  SearchWrapper,
  Input,
  FilterWrapper,
  SelectWrapper,
  SelectLabel,
  Select,
  Option,
  MainContent,
  DownloadButton,
  EyeButton,
  ActionsWrapper
} from './styles'
import PaginationFooter from '../../components/PaginationFooter'

const STATUS = {
  incompleto: 'Incompleto',
  'no referido': 'No referido',
  diagnosticado: 'Diagnosticado',
  pendiente: 'Informe pendiente',
  informado: 'Informado',
  informando: 'Informando'
}

const SORT_FIELD_TYPES = {
  dni: 'number',
  paciente: 'text',
  tecnico: 'text',
  nodoCaptura: 'text',
  institucion: 'text',
  fechaCaptura: 'date',
  fechaInforme: 'date',
  estado: 'text',
  tipo: 'text',
  resumenDiagnostico: 'text'
}

const getPatientFullName = (singleCase) => {
  const firstName = singleCase && singleCase.paciente && singleCase.paciente.nombre ? singleCase.paciente.nombre : ''
  const lastName = singleCase && singleCase.paciente && singleCase.paciente.apellido ? singleCase.paciente.apellido : ''
  const fullName = `${firstName} ${lastName}`.trim()
  return fullName || '-'
}

const getTechnicianFullName = (singleCase) => {
  const firstName = singleCase && singleCase.tecnico && singleCase.tecnico.nombre ? singleCase.tecnico.nombre : ''
  const lastName = singleCase && singleCase.tecnico && singleCase.tecnico.apellido ? singleCase.tecnico.apellido : ''
  const fullName = `${firstName} ${lastName}`.trim()
  return fullName || '-'
}

const getLatestReportDate = (singleCase) => {
  if (!singleCase || !Array.isArray(singleCase.informes)) {
    return null
  }

  return singleCase.informes.reduce((latestDate, singleReport) => {
    const reportDate = singleReport && singleReport.fecha_fin ? singleReport.fecha_fin : null
    if (!reportDate) {
      return latestDate
    }
    if (!latestDate) {
      return reportDate
    }
    return new Date(reportDate) > new Date(latestDate) ? reportDate : latestDate
  }, null)
}

const normalizeSortValue = (value, type) => {
  if (value === undefined || value === null || value === '') {
    return null
  }

  if (type === 'number') {
    const parsedNumber = Number(value)
    return Number.isNaN(parsedNumber) ? null : parsedNumber
  }

  if (type === 'date') {
    const parsedDate = new Date(value).getTime()
    return Number.isNaN(parsedDate) ? null : parsedDate
  }

  return `${value}`.toLowerCase().trim()
}

const compareValues = (firstValue, secondValue) => {
  if (firstValue === secondValue) return 0
  if (firstValue === null) return 1
  if (secondValue === null) return -1

  if (typeof firstValue === 'number' && typeof secondValue === 'number') {
    return firstValue - secondValue
  }

  return `${firstValue}`.localeCompare(`${secondValue}`, 'es', { sensitivity: 'base' })
}

const renderDiagnosis = (row) => {
  const singleCase = row.originalCase
  const isInformado = singleCase.estado === 'informado'

  if (isInformado && singleCase.requiere_recaptura) {
    return <DiagnosisBadge tone='neutral' label={<>Requiere<br/>recaptura</>} icon={<CircleAlert size={22} />} />
  }

  const dxString = isInformado ? singleCase.resumen_DX_MANUAL : singleCase.resumen_DX_IA

  if (!dxString || typeof dxString !== 'string') {
    return <DiagnosisBadge tone='unknown' label='-' />
  }

  const dxLower = dxString.toLowerCase()
  const isReferible = dxLower.includes('referible') && !dxLower.includes('no referible')
  const isNoReferible = dxLower.includes('no referible') || dxLower.includes('normal')

  const source = isInformado ? 'Profesional' : 'IA'

  if (isReferible) {
    return <DiagnosisBadge tone='referible' source={source} label={dxString} icon={<CircleAlert size={22} />} />
  }

  if (isNoReferible) {
    return <DiagnosisBadge tone='normal' source={source} label={dxString} icon={<CircleCheck size={22} />} />
  }

  return <DiagnosisBadge tone='unknown' source={source} label={dxString} />
}

export function Evaluations () {
  const { user, evaluations } = useSelector(state => state)
  const history = useHistory()
  const { formatDate } = useDateFormatter()
  const { setCase } = useSetCase()
  const { openPDF } = useOpenPDF()
  const { fetchEntities } = useAdminAPI()

  const [filters, setFilters] = useState({
    status: 'all',
    search: '',
    period: 0
  })
  const [nodesById, setNodesById] = useState({})
  const [institutionsById, setInstitutionsById] = useState({})
  const [sortConfig, setSortConfig] = useState({
    key: 'fechaCaptura',
    direction: 'desc'
  })

  // Estado propio del listado tecnico por alcance (separado de user.evaluations)
  const [scope, setScope] = useState('node')
  const [isLoadingTechnic, setIsLoadingTechnic] = useState(false)

  const {
    setCasesPerPage,
    setCurrentPage,
    resetDataset,
    search,
    statistics,
    currentPage,
    cases,
    casesPerPage
  } = useCases()

  const handleDownloadPDF = async (singleCase) => {
    if (!pdfDisponible(singleCase)) return
    try {
      await openPDF(`estudios/descargar/${singleCase.id}`)
    } catch (error) {
      console.error('Error al descargar el PDF:', error)
    }
  }

  const handleViewStudy = async (singleCase) => {
    try {
      await setCase(singleCase)
      history.push('/new-report?mode=readonly')
    } catch (error) {
      console.error(error)
    }
  }

  const handleChangeFilter = (event) => {
    const updatedFilters = { ...filters, [event.target.name]: event.target.value }
    setFilters(updatedFilters)
    search(updatedFilters)
    setCurrentPage(1)
  }

  const handleChangeRowsPerPage = (event) => {
    setCasesPerPage(Number(event.target.value))
    setCurrentPage(1)
  }

  const handleSortChange = ({ key, direction }) => {
    setSortConfig({ key, direction })
    setCurrentPage(1)
  }

  const handleScopeChange = (event) => {
    setScope(event.target.value)
    setCurrentPage(1)
    setFilters({ status: 'all', search: '', period: 0 })
  }

  // Carga remota del listado tecnico segun alcance
  const loadTechnicDataset = useCallback(async (currentScope) => {
    setIsLoadingTechnic(true)
    try {
      const data = await fetchEstudiosTecnicoPorAlcance(user.token, currentScope)
      const dataset = Array.isArray(data) ? data : []
      resetDataset(dataset)
    } catch (error) {
      console.error('Error al cargar estudios del nodo:', error)
      resetDataset([])
    } finally {
      setIsLoadingTechnic(false)
    }
  }, [user.token, resetDataset])

  // Para el rol tecnico: cargar desde backend segun alcance
  useEffect(() => {
    if (user.role !== 'technical') {
      return
    }
    loadTechnicDataset(scope)
  }, [user.role, scope, loadTechnicDataset])

  // Para los otros roles: usar user.evaluations como antes
  useEffect(() => {
    if (user.role === 'technical') {
      return
    }
    resetDataset(user.evaluations || [])
  }, [resetDataset, user.evaluations, user.role])

  useEffect(() => {
    if (user.role !== 'ophthalmologist') {
      return
    }

    const fetchReferences = async () => {
      try {
        const [nodes, institutions] = await Promise.all([
          fetchEntities('nodos-captura'),
          fetchEntities('instituciones')
        ])

        const nodesMap = (Array.isArray(nodes) ? nodes : []).reduce((accumulator, node) => {
          accumulator[node.idNodoCaptura] = node
          return accumulator
        }, {})

        const institutionsMap = (Array.isArray(institutions) ? institutions : []).reduce((accumulator, institution) => {
          accumulator[institution.idInstitucion] = institution
          return accumulator
        }, {})

        setNodesById(nodesMap)
        setInstitutionsById(institutionsMap)
      } catch (error) {
        console.error('Error al cargar nodos e instituciones:', error)
      }
    }

    fetchReferences()
  }, [fetchEntities, user.role])

  const mappedRows = useMemo(() => {
    if (!Array.isArray(cases)) {
      return []
    }

    if (user.role === 'ophthalmologist') {
      return cases.filter(singleCase => singleCase.estado === 'informado').map(singleCase => {
        const node = nodesById[singleCase.capturadoEn]
        const institutionId = singleCase.idInstitucion || (node && node.idInstitucion)
        const institution = institutionsById[institutionId]

        return {
          id: singleCase.id,
          dni: singleCase && singleCase.paciente && singleCase.paciente.dni ? `${singleCase.paciente.dni}` : '-',
          paciente: getPatientFullName(singleCase),
          nodoCaptura: (singleCase && singleCase.nodoCaptura && singleCase.nodoCaptura.nombre) ? singleCase.nodoCaptura.nombre : (node && node.nombre ? node.nombre : (singleCase.capturadoEn ? `Nodo ${singleCase.capturadoEn}` : '-')),
          institucion: institution && institution.nombre ? institution.nombre : (institutionId ? `Institución ${institutionId}` : '-'),
          fechaCaptura: singleCase.fecha || null,
          fechaInforme: getLatestReportDate(singleCase),
          originalCase: singleCase
        }
      })
    }

    if (user.role === 'technical') {
      return cases.map(singleCase => {
        const isInformado = singleCase.estado === 'informado'
        let dxString = isInformado ? singleCase.resumen_DX_MANUAL : singleCase.resumen_DX_IA
        if (isInformado && singleCase.requiere_recaptura) {
          dxString = 'Requiere recaptura'
        }

        return {
          id: singleCase.id,
          dni: singleCase && singleCase.paciente && singleCase.paciente.dni ? `${singleCase.paciente.dni}` : '-',
          paciente: getPatientFullName(singleCase),
          tecnico: getTechnicianFullName(singleCase),
          fechaCaptura: singleCase.fecha || null,
          tipo: EXAM_TYPE_TEXT[singleCase.tipo] || '-',
          estado: STATUS[singleCase.estado] || singleCase.estado,
          resumenDiagnostico: dxString || '-',
          originalCase: singleCase
        }
      })
    }

    return cases.map(singleCase => {
      const isInformado = singleCase.estado === 'informado'
      let dxString = isInformado ? singleCase.resumen_DX_MANUAL : singleCase.resumen_DX_IA
      if (isInformado && singleCase.requiere_recaptura) {
        dxString = 'Requiere recaptura'
      }

      return {
        id: singleCase.id,
        dni: singleCase && singleCase.paciente && singleCase.paciente.dni ? `${singleCase.paciente.dni}` : '-',
        paciente: getPatientFullName(singleCase),
        fechaCaptura: singleCase.fecha || null,
        tipo: EXAM_TYPE_TEXT[singleCase.tipo] || '-',
        estado: STATUS[singleCase.estado] || singleCase.estado,
        resumenDiagnostico: dxString || '-',
        originalCase: singleCase
      }
    })
  }, [cases, institutionsById, nodesById, user.role])

  const sortedRows = useMemo(() => {
    const fieldType = SORT_FIELD_TYPES[sortConfig.key] || 'text'
    const rowsToSort = [...mappedRows]

    rowsToSort.sort((firstRow, secondRow) => {
      const firstValue = normalizeSortValue(firstRow[sortConfig.key], fieldType)
      const secondValue = normalizeSortValue(secondRow[sortConfig.key], fieldType)
      const comparisonResult = compareValues(firstValue, secondValue)
      return sortConfig.direction === 'asc' ? comparisonResult : -comparisonResult
    })

    return rowsToSort
  }, [mappedRows, sortConfig])

  const rowsPerPage = Number(casesPerPage) || 10

  const listOfPages = useMemo(() => {
    const pageCount = Math.ceil(sortedRows.length / rowsPerPage)
    return Array.from({ length: Math.max(1, pageCount) }, (_, index) => index + 1)
  }, [rowsPerPage, sortedRows.length])

  const rowsToShow = useMemo(() => {
    const indexOfLastCase = currentPage * rowsPerPage
    const indexOfFirstCase = indexOfLastCase - rowsPerPage
    return sortedRows.slice(indexOfFirstCase, indexOfLastCase)
  }, [currentPage, rowsPerPage, sortedRows])

  useEffect(() => {
    if (listOfPages.length > 0 && currentPage > listOfPages.length) {
      setCurrentPage(listOfPages.length)
    }
  }, [currentPage, listOfPages, setCurrentPage])

  const tableColumns = useMemo(() => {
    if (user.role === 'ophthalmologist') {
      return [
        { key: 'dni', label: 'DNI', sortable: true, width: '0.9fr' },
        { key: 'paciente', label: 'Paciente', sortable: true, width: '1.2fr' },
        { key: 'nodoCaptura', label: 'Nodo de Captura', sortable: true, width: '1.1fr' },
        { key: 'institucion', label: 'Institución', sortable: true, width: '1.2fr' },
        {
          key: 'fechaCaptura',
          label: 'Fecha de Captura',
          sortable: true,
          width: '1fr',
          render: (row) => row.fechaCaptura ? formatDate(row.fechaCaptura) : '-'
        },
        {
          key: 'fechaInforme',
          label: 'Fecha de Informe',
          sortable: true,
          width: '1fr',
          render: (row) => row.fechaInforme ? formatDate(row.fechaInforme) : '-'
        },
        {
          key: 'acciones',
          label: 'Acciones',
          sortable: false,
          align: 'center',
          width: '0.8fr',
          render: (row) => (
            <ActionsWrapper>
              <EyeButton
                onClick={() => handleViewStudy(row.originalCase)}
                title='Ver estudio'
                aria-label='Ver estudio'
              >
                <Eye size={18} aria-hidden='true' />
              </EyeButton>
              <DownloadButton
                onClick={() => handleDownloadPDF(row.originalCase)}
                disabled={!pdfDisponible(row.originalCase)}
                title={pdfDisponible(row.originalCase) ? 'Descargar PDF' : 'Preparando PDF...'}
                aria-label={pdfDisponible(row.originalCase) ? 'Descargar PDF' : 'Preparando PDF...'}
              >
                <Download size={18} aria-hidden='true' />
              </DownloadButton>
            </ActionsWrapper>
          )
        }
      ]
    }

    const patientCols = [
      {
        key: 'fechaCaptura',
        label: 'Fecha',
        sortable: true,
        width: '0.8fr',
        render: (row) => row.fechaCaptura ? formatDate(row.fechaCaptura) : '-'
      },
      { key: 'tipo', label: 'Tipo de examen', sortable: true, width: '1fr' },
      { key: 'estado', label: 'Estado', sortable: true, width: '1fr' },
      { key: 'resumenDiagnostico', label: 'Resumen diagnóstico', sortable: true, width: '1.3fr', render: renderDiagnosis },
      {
        key: 'acciones',
        label: 'Acciones',
        sortable: false,
        align: 'center',
        width: '0.8fr',
        render: (row) => (
          <ActionsWrapper>
            <EyeButton
              onClick={() => handleViewStudy(row.originalCase)}
              title='Ver estudio'
              aria-label='Ver estudio'
            >
              <Eye size={18} aria-hidden='true' />
            </EyeButton>
            <DownloadButton
              onClick={() => handleDownloadPDF(row.originalCase)}
              disabled={!pdfDisponible(row.originalCase)}
              title={pdfDisponible(row.originalCase) ? 'Descargar PDF' : 'Preparando PDF...'}
              aria-label={pdfDisponible(row.originalCase) ? 'Descargar PDF' : 'Preparando PDF...'}
            >
              <Download size={18} aria-hidden='true' />
            </DownloadButton>
          </ActionsWrapper>
        )
      }
    ]

    const technicalCols = [
      {
        key: 'fechaCaptura',
        label: 'Fecha',
        sortable: true,
        width: '0.8fr',
        render: (row) => row.fechaCaptura ? formatDate(row.fechaCaptura) : '-'
      },
      { key: 'paciente', label: 'Paciente', sortable: true, width: '1.1fr' },
      { key: 'dni', label: 'DNI', sortable: true, width: '0.8fr' },
      { key: 'tecnico', label: 'Técnico', sortable: true, width: '1fr' },
      { key: 'tipo', label: 'Tipo de examen', sortable: true, width: '1fr' },
      { key: 'estado', label: 'Estado', sortable: true, width: '1fr' },
      { key: 'resumenDiagnostico', label: 'Resumen diagnóstico', sortable: true, width: '1.2fr', render: renderDiagnosis },
      {
        key: 'acciones',
        label: 'Acciones',
        sortable: false,
        align: 'center',
        width: '0.5fr',
        render: (row) => (
          <ActionsWrapper>
            <DownloadButton
              onClick={() => handleDownloadPDF(row.originalCase)}
              disabled={!pdfDisponible(row.originalCase)}
              title={pdfDisponible(row.originalCase) ? 'Descargar PDF' : 'Preparando PDF...'}
              aria-label={pdfDisponible(row.originalCase) ? 'Descargar PDF' : 'Preparando PDF...'}
            >
              <Download size={18} aria-hidden='true' />
            </DownloadButton>
          </ActionsWrapper>
        )
      }
    ]

    return user.role === 'patient' ? patientCols : technicalCols
  }, [user.role, formatDate])

  const tableMinWidth = useMemo(() => {
    if (user.role === 'patient') return '800px'
    if (user.role === 'technical') return '1100px'
    return '1120px'
  }, [user.role])

  const emptyStateMessage = useMemo(() => {
    if (user.role === 'patient') return 'Aún no tenés estudios disponibles'
    if (user.role === 'technical') {
      if (isLoadingTechnic) return 'Cargando estudios...'
      return scope === 'node' ? 'No hay estudios registrados en este nodo' : 'No tenés estudios registrados'
    }
    return 'No hay estudios informados para mostrar'
  }, [user.role, isLoadingTechnic, scope])

  return (
    <Container>
      <SessionExpired />

      {user.role === 'patient' && (
        <PatientInformation study={evaluations} patientData={user.data} />
      )}

      <Header>
        <TitleWrapper>
          <TitleIcon src={Statistics} alt='statistics-icon' />
          <Title> {user.role === 'ophthalmologist' ? 'Tus estudios informados' : 'Listado de evaluaciones'} </Title>
        </TitleWrapper>

        {user.role !== 'patient' && (
          <MonthlyCasesWrapper>
            <MonthlyTitle> Realizados este mes </MonthlyTitle>
            <MonthlyValues>
              <MonthlyTotal> {statistics.total} </MonthlyTotal>
              <MonthlyPercentageWrapper>
                <MonthlyPercentageIcon src={StatusArrow} alt='percentage-status' />
                <MonthlyPercentage> {statistics.percentage} % </MonthlyPercentage>
              </MonthlyPercentageWrapper>
            </MonthlyValues>
          </MonthlyCasesWrapper>
        )}
      </Header>

      <Subheader>
        <SearchWrapper onSubmit={(e) => e.preventDefault()}>
          <Input
            name='search'
            value={filters.search}
            onChange={handleChangeFilter}
            placeholder={user.role === 'patient' ? 'Buscar...' : 'Buscar por datos de paciente...'}
          />
        </SearchWrapper>

        <FilterWrapper>
          {user.role === 'technical' && (
            <SelectWrapper>
              <SelectLabel> Alcance </SelectLabel>
              <Select name='scope' value={scope} onChange={handleScopeChange}>
                <Option value='node'> Todo el nodo </Option>
                <Option value='own'> Solo mis estudios </Option>
              </Select>
            </SelectWrapper>
          )}

          <SelectWrapper>
            <SelectLabel> Periodo </SelectLabel>
            <Select name='period' value={filters.period} onChange={handleChangeFilter}>
              <Option value={0}> Todos </Option>
              <Option value={1}> Último mes </Option>
              <Option value={3}> Últimos 3 meses </Option>
              <Option value={12}> Último año </Option>
            </Select>
          </SelectWrapper>

          {user.role !== 'ophthalmologist' && (
            <SelectWrapper>
              <SelectLabel> Estado </SelectLabel>
              <Select name='status' value={filters.status} onChange={handleChangeFilter}>
                <Option value='all'> Todos </Option>
                {Object.keys(STATUS).map((status, index) => (
                  <Option value={status} key={index}> {STATUS[status]} </Option>
                ))}
              </Select>
            </SelectWrapper>
          )}
        </FilterWrapper>
      </Subheader>

      <MainContent>
        <SortableTable
          title={user.role === 'ophthalmologist' ? `Estudios Informados (${mappedRows.length})` : undefined}
          subtitle='Hacé clic en los encabezados para ordenar por columna'
          columns={tableColumns}
          rows={rowsToShow}
          rowKey='id'
          sortBy={sortConfig.key}
          sortDirection={sortConfig.direction}
          onSortChange={handleSortChange}
          emptyMessage={emptyStateMessage}
          minWidth={tableMinWidth}
        />

        <PaginationFooter
          currentPage={currentPage}
          listOfPages={listOfPages}
          handleChangePage={setCurrentPage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </MainContent>
    </Container>
  )
}

export default Evaluations
